import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  Stack,
  Button,
  Select,
  MenuItem,
  FormControl,
  Pagination,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  MonetizationOnOutlined,
  AccountBalanceWalletOutlined,
  Print,
  Receipt,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import EditMedicinePaymentListDialog from "./EditMedicinePaymentListDialog";
import { useNavigate } from "react-router-dom";
import PrintMedicinePayment from "./PrintMedicinePayment";

const MedicinePaymentList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [totalPaid, setTotalPaid] = useState(0);
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);

  const { data: companies, loading: loadingCompanies } = useFetchObjects(
    ["medicine-companies"],
    `medicine-companies/`,
    token
  );

  const {
    data: payments,
    refetch,
    isLoading,
    isError,
  } = useFetchObjects(
    [
      "purchase-payments",
      query,
      dateFilter,
      selectedCompany,
      page,
      rowsPerPage,
    ],
    `purchase-payments/?search=${query}&date=${dateFilter}&medicine_company=${selectedCompany}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("purchase-payments", token);

  useEffect(() => {
    refetch();
  }, [query, dateFilter, selectedCompany, refetch, page, rowsPerPage]);

  useEffect(() => {
    if (payments && typeof payments.count === "number") {
      setTotalPayments(payments.count);
    }
  }, [payments]);

  useEffect(() => {
    if (selectedCompany) {
      const company = companies?.find((c) => c.id === selectedCompany);
      if (company) {
        setTotalPaid(company.total_paid);
        setRemainingBalance(company.remaining_balance);
        setTotalAmount(company.total_amount || 0);
      }
    } else {
      setTotalPaid(0);
      setRemainingBalance(0);
      setTotalAmount(0);
    }
  }, [selectedCompany, companies]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleEditClick = (payment) => {
    setCurrentPayment(payment);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPayment(null);
    refetch();
  };

  const handlePrintClick = (payment) => {
    setPrintData(payment);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  return (
    <Grid container spacing={4} mt={2} mb={4} pb={5}>
      <Grid item xs={12}>
        <Box
          padding="20px"
          backgroundColor="white"
          boxShadow={2}
          borderRadius={2}
        >
          <Box display="flex" justifyContent="space-between" mb={3}>
            <Typography variant="h6" fontWeight={600} color="primary">
              {t("finance.medicinePaymentList", "Medicine Payments")}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/finance/medicine-payment/add")}
              sx={{
                textTransform: "none",
                fontWeight: "500",
                paddingX: 2,
                paddingY: 1,
                borderRadius: "6px",
                boxShadow: 1,
                transition: "all 0.3s ease",
              }}
            >
              {t("finance.addNew", "Add New")}
            </Button>
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
            <TextField
              label={t("finance.searchByPurchase", "Search by Purchase")}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{ endAdornment: <Search color="action" /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <TextField
              label={t("finance.filterByDate", "Filter by Date")}
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === "") {
                    return <em>{t("finance.selectCompany", "Select Company")}</em>;
                  }
                  const company = companies?.find((c) => c.id === selected);
                  return company ? (
                    company.name
                  ) : (
                    <em>{t("finance.selectCompany", "Select Company")}</em>
                  );
                }}
              >
                <MenuItem value="">
                  <em>{t("finance.selectCompany", "Select Company")}</em>
                </MenuItem>
                {companies?.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {selectedCompany && (
            <Box mb={3}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Box
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    bgcolor="#f5f5f5"
                    borderRadius={1}
                  >
                    <Receipt color="info" fontSize="small" />
                    <Box ml={1.5}>
                      <Typography variant="caption" color="textSecondary">
                        {t("finance.companyName", "Company")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {companies?.find(c => c.id === selectedCompany)?.name || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    bgcolor="#f5f5f5"
                    borderRadius={1}
                  >
                    <MonetizationOnOutlined color="primary" fontSize="small" />
                    <Box ml={1.5}>
                      <Typography variant="caption" color="textSecondary">
                        {t("finance.totalAmount", "Total Amount")}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="primary"
                        fontWeight={500}
                      >
                        ؋ {totalAmount?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    bgcolor="#f5f5f5"
                    borderRadius={1}
                  >
                    <MonetizationOnOutlined color="success" fontSize="small" />
                    <Box ml={1.5}>
                      <Typography variant="caption" color="textSecondary">
                        {t("finance.totalPaid", "Total Paid")}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="success.main"
                        fontWeight={500}
                      >
                        ؋ {totalPaid?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    bgcolor="#f5f5f5"
                    borderRadius={1}
                  >
                    <AccountBalanceWalletOutlined
                      color="error"
                      fontSize="small"
                    />
                    <Box ml={1.5}>
                      <Typography variant="caption" color="textSecondary">
                        {t("finance.remainingBalance", "Remaining")}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="error"
                        fontWeight={500}
                      >
                        ؋ {remainingBalance?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    p={1.5}
                    bgcolor="#f5f5f5"
                    borderRadius={1}
                  >
                    <Receipt color="warning" fontSize="small" />
                    <Box ml={1.5}>
                      <Typography variant="caption" color="textSecondary">
                        {t("finance.paymentsCount", "Payments")}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="warning.main"
                        fontWeight={500}
                      >
                        {totalPayments}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {isLoading ? (
            <Box textAlign="center" mt={4}>
              <CircularProgress />
              <Typography variant="body2">
                {t("finance.loading", "Loading...")}
              </Typography>
            </Box>
          ) : isError ? (
            <Box textAlign="center" color="red" mt={4}>
              <Typography variant="body2">
                {t("finance.errorFetchingData", "Error fetching data")}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ boxShadow: 1, borderRadius: 1 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: "#fafafa" }}>
                    <TableCell align="center" width="5%">
                      #
                    </TableCell>
                    <TableCell align="center">
                      {t("finance.company", "Company")}
                    </TableCell>
                    <TableCell align="center">
                      {t("finance.invoiceNumber", "Invoice Number")}
                    </TableCell>
                    <TableCell align="center">
                      {t("finance.amountPaid", "Amount Paid")}
                    </TableCell>
                    <TableCell align="center">
                      {t("finance.paymentDate", "Payment Date")}
                    </TableCell>
                    <TableCell align="center" width="15%">
                      {t("finance.bills", "Bills")}
                    </TableCell>
                    <TableCell align="center" width="15%">
                      {t("finance.actions", "Actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments?.results?.map((payment, index) => (
                    <TableRow key={payment.id}>
                      <TableCell align="center">
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="center">
                        {payment.medicine_company_display || "-"}
                      </TableCell>
                      <TableCell align="center">
                        {payment.invoice_number ? (
                          <Chip
                            label={payment.invoice_number}
                            size="small"
                            color="primary"
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight={500}>
                          ؋ {payment.amount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {new Date(payment.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center" sx={{ maxWidth: 200, py: 1 }}>
                        {payment.images?.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              overflowX: "auto",
                              maxWidth: 200,
                              maxHeight: 60,
                              alignItems: "center",
                              py: 0.5,
                            }}
                          >
                            {payment.images.map((img) => (
                              <Box
                                key={img.id}
                                component="img"
                                src={img.image}
                                alt="bill"
                                sx={{
                                  width: 50,
                                  height: 50,
                                  objectFit: "cover",
                                  borderRadius: 1,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  boxShadow: 1,
                                }}
                                onClick={() => window.open(img.image, "_blank")}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            {t("finance.noBills", "No bills")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t("finance.edit", "Edit")}>
                          <IconButton
                            onClick={() => handleEditClick(payment)}
                            color="primary"
                            size="small"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("finance.delete", "Delete")}>
                          <IconButton
                            onClick={() => handleDelete(payment.id)}
                            color="error"
                            size="small"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("finance.print", "Print")}>
                          <IconButton
                            onClick={() => handlePrintClick(payment)}
                            color="info"
                            size="small"
                          >
                            <Print fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body2" mr={1}>
                {t("finance.rowsPerPage", "Rows per page")}:
              </Typography>
              <FormControl variant="outlined" size="large">
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{
                    width: 100,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                >
                  {[10, 25, 50, 100, 500, 1000].map((rows) => (
                    <MenuItem key={rows} value={rows}>
                      {rows}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalPayments / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>

          {currentPayment && (
            <EditMedicinePaymentListDialog
              open={openDialog}
              onClose={handleCloseDialog}
              payment={currentPayment}
              token={token}
            />
          )}

          {printData && (
            <div ref={printRef} style={{ display: "none" }}>
              <PrintMedicinePayment data={printData} />
            </div>
          )}
        </Box>
      </Grid>
      <ConfirmDialog />
    </Grid>
  );
};

export default MedicinePaymentList;